import api from '../httpClient';

export type ConsultationMode = 'online' | 'in_person' | 'both';

export interface NutritionistConsultationType {
  id: string;
  name: string;
  description: string;
  durationMinutes: number;
  mode: ConsultationMode;
  rate: number;
  isActive: boolean;
  raw?: Record<string, unknown>;
}

export interface CreateNutritionistConsultationTypePayload {
  name: string;
  description: string;
  durationMinutes: number;
  mode: ConsultationMode;
  rate: number;
  isActive: boolean;
}

export type UpdateNutritionistConsultationTypePayload = CreateNutritionistConsultationTypePayload;

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};

const unwrapApiData = (value: unknown): unknown => {
  const record = asRecord(value);
  if ('data' in record) {
    return unwrapApiData(record.data);
  }
  return value;
};

const readString = (...values: unknown[]): string => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
  }
  return '';
};

const readNumber = (...values: unknown[]): number => {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === 'string' && value.trim()) {
      const parsed = Number(value.replace(/[^0-9.-]/g, ''));
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }
  return 0;
};

const readBoolean = (...values: unknown[]): boolean => {
  for (const value of values) {
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (normalized === 'true' || normalized === 'active') {
        return true;
      }
      if (normalized === 'false' || normalized === 'inactive') {
        return false;
      }
    }
  }
  return true;
};

const normalizeConsultationMode = (value: unknown): ConsultationMode => {
  const normalized = String(value || 'both').trim().toLowerCase().replace(/[-\s]+/g, '_');
  if (normalized === 'online') {
    return 'online';
  }
  if (normalized === 'in_person' || normalized === 'offline' || normalized === 'inperson') {
    return 'in_person';
  }
  return 'both';
};

const extractArray = (value: unknown, keys: string[]): unknown[] => {
  const payload = unwrapApiData(value);
  if (Array.isArray(payload)) {
    return payload;
  }

  const record = asRecord(payload);
  for (const key of keys) {
    const nested = record[key];
    if (Array.isArray(nested)) {
      return nested;
    }
  }

  return [];
};

const extractEntity = (value: unknown, keys: string[]): unknown => {
  const payload = unwrapApiData(value);
  const record = asRecord(payload);
  for (const key of keys) {
    if (record[key]) {
      return record[key];
    }
  }
  return payload;
};

export const normalizeNutritionistConsultationType = (value: unknown): NutritionistConsultationType => {
  const record = asRecord(value);

  return {
    id: readString(record.id, record._id, record.typeId, record.consultationTypeId),
    name: readString(record.name, record.title),
    description: readString(record.description),
    durationMinutes: readNumber(
      record.durationMinutes,
      record.duration_minutes,
      record.sessionDurationMinutes,
      record.sessionDuration,
      record.duration,
    ),
    mode: normalizeConsultationMode(record.mode ?? record.deliveryMode ?? record.delivery_mode),
    rate: readNumber(record.rate, record.price, record.amount),
    isActive: readBoolean(record.isActive, record.is_active, record.active, record.status),
    raw: record,
  };
};

export const getNutritionistConsultationTypes = async (): Promise<NutritionistConsultationType[]> => {
  const response = await api.get('/nutritionists/consultation-types');
  return extractArray(response.data, ['consultationTypes', 'types', 'items', 'results']).map(
    normalizeNutritionistConsultationType,
  );
};

export const createNutritionistConsultationType = async (
  payload: CreateNutritionistConsultationTypePayload,
): Promise<NutritionistConsultationType> => {
  const response = await api.post('/nutritionists/consultation-types', payload);
  return normalizeNutritionistConsultationType(extractEntity(response.data, ['consultationType', 'type', 'item']));
};

export const updateNutritionistConsultationType = async (
  typeId: string,
  payload: UpdateNutritionistConsultationTypePayload,
): Promise<NutritionistConsultationType> => {
  const response = await api.put(`/nutritionists/consultation-types/${encodeURIComponent(typeId)}`, payload);
  return normalizeNutritionistConsultationType(extractEntity(response.data, ['consultationType', 'type', 'item']));
};

export const deleteNutritionistConsultationType = async (typeId: string): Promise<void> => {
  await api.delete(`/nutritionists/consultation-types/${encodeURIComponent(typeId)}`);
};
